/**
 * QuoteApprovalDetailPanel (KAN-169)
 *
 * Compact inline panel shown when a pending quote approval request is selected
 * in the Approvals & Modifications page. Shows trade summary and approve/reject actions.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Eye } from 'lucide-react';
import { quoteApprovalService } from '../../services/api/quoteApprovalService';
import TradeDetailViewModal from './TradeDetailViewModal';

interface Props {
  trade: Record<string, any>;
  clientId: string;
  onApproveSuccess: () => void;
  onRejectSuccess: () => void;
  onError: (message: string) => void;
  onClose: () => void;
}

const QuoteApprovalDetailPanel: React.FC<Props> = ({
  trade,
  clientId,
  onApproveSuccess,
  onRejectSuccess,
  onError,
  onClose,
}) => {
  const { t } = useTranslation();
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const qa = trade.quoteApproval || {};
  const entityId = trade.entityId || trade.OurPartyId || clientId;

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (value: any): string => {
    if (!value) return '-';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const response = await quoteApprovalService.approveQuote(entityId, trade.id);
      if (response.success) {
        onApproveSuccess();
      } else {
        onError(response.message || 'Failed to approve');
      }
    } catch (err: any) {
      onError(err.message || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      onError(t('modifications.detail.rejectReasonRequired', 'Rejection reason is required'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await quoteApprovalService.rejectQuote(entityId, trade.id, rejectReason.trim());
      if (response.success) {
        onRejectSuccess();
      } else {
        onError(response.message || 'Failed to reject');
      }
    } catch (err: any) {
      onError(err.message || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset reject form when trade changes
  React.useEffect(() => {
    setRejectReason('');
    setShowRejectForm(false);
  }, [trade.id]);

  const productTypeDisplay = (() => {
    const pt = trade.ProductType || '';
    if (pt === 'Forward') {
      if (trade.IsForwardStarting) return 'Forward (Starting)';
      if (trade.IsAverageRateForward) return 'Forward (Asiático)';
    }
    return pt;
  })();

  const allRows: { label: string; value: string }[] = [
    { label: t('modifications.columns.tradeNumber', 'Trade #'), value: trade.TradeNumber || '' },
    { label: t('modifications.columns.bankTradeNumber', 'Bank Trade #'), value: trade.bankTradeNumber || '' },
    { label: t('modifications.columns.entity', 'Entity'), value: trade.OurParty || '' },
    { label: t('modifications.columns.counterparty', 'Counterparty'), value: trade.CounterpartyName || '' },
    { label: t('modifications.columns.productType', 'Product'), value: productTypeDisplay },
    { label: t('modifications.columns.direction', 'Direction'), value: trade.Direction || '' },
    { label: t('modifications.columns.tradeDate', 'Trade Date'), value: formatDate(trade.TradeDate) },
    { label: t('modifications.columns.maturityDate', 'Maturity Date'), value: formatDate(trade.MaturityDate) },
    { label: t('tradeBooking.fields.currencyPair', 'Currency Pair'), value: trade.Currency1 && trade.Currency2 ? `${trade.Currency1}/${trade.Currency2}` : '' },
    { label: `${t('modifications.columns.amount', 'Amount')} ${trade.Currency1 || ''}`, value: formatNumber(trade.QuantityCurrency1) },
    { label: `${t('tradeBooking.fields.contraAmount', 'Contra Amount')} ${trade.Currency2 || ''}`, value: formatNumber(trade.QuantityCurrency2) },
    { label: t('modifications.columns.spotPrice', 'Spot'), value: formatNumber(trade.SpotPrice) },
    { label: t('modifications.columns.fwdPts', 'Fwd Pts'), value: formatNumber(trade.ForwardPoints) },
    { label: t('modifications.columns.price', 'Price'), value: formatNumber(trade.Price) },
    { label: t('modifications.columns.settlementType', 'Settlement Type'), value: trade.SettlementType || '' },
    { label: t('modifications.fields.settlementCurrency', 'Settlement Currency'), value: trade.SettlementCurrency || '' },
    { label: t('modifications.fields.fixingReference', 'Fixing Reference'), value: trade.FixingReference || '' },
    { label: t('tradeBooking.fields.paymentDate', 'Payment Date'), value: formatDate(trade.PaymentDate) },
    { label: t('modifications.fields.grouping1', 'Grouping 1'), value: trade.Grouping1 || '' },
    { label: t('modifications.fields.grouping2', 'Grouping 2'), value: trade.Grouping2 || '' },
    { label: t('modifications.fields.grouping3', 'Grouping 3'), value: trade.Grouping3 || '' },
    { label: t('modifications.fields.hedgeObject', 'Hedge Object'), value: trade.HedgeObject || '' },
  ];

  // Only show fields that have a value
  const summaryRows = allRows.filter((row) => row.value && row.value !== '-');

  return (
    <div className="mod-panel">
      {/* Header bar */}
      <div className="mod-panel-header">
        <div className="mod-panel-header-left">
          <div className="mod-panel-header-row">
            <span className="mod-panel-trade-num">
              {t('modifications.detail.title', { tradeNumber: trade.TradeNumber })}
            </span>
            <span className="mod-panel-mod-type">
              {t('modifications.types.draftTradeApproval', 'Trade Approval')}
            </span>
          </div>
          {qa.requestedByName && (
            <span className="mod-panel-meta">
              {t('modifications.quoteApproval.requestedBy', {
                defaultValue: 'Requested by {{name}} on {{date}}',
                name: qa.requestedByName,
                date: formatDate(qa.requestedAt),
              })}
            </span>
          )}
        </div>
        <div className="mod-panel-header-actions">
          <button
            className="mod-panel-close"
            onClick={() => setShowDetailModal(true)}
            title={t('modifications.quoteApproval.viewDetails', 'View Details')}
          >
            <Eye size={16} />
          </button>
          <button className="mod-panel-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Trade summary */}
      {!showRejectForm && (
        <table className="mod-panel-table">
          <thead>
            <tr>
              <th>{t('modifications.detail.fieldLabel', 'Field')}</th>
              <th>{t('modifications.quoteApproval.valueLabel', 'Value')}</th>
            </tr>
          </thead>
          <tbody>
            {summaryRows.map((row) => (
              <tr key={row.label}>
                <td className="mod-panel-field-name">{row.label}</td>
                <td className="mod-panel-current">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Reject reason form */}
      {showRejectForm && (
        <div className="mod-panel-reject-form">
          <textarea
            className="mod-panel-textarea"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('modifications.detail.rejectReasonPlaceholder', 'Enter reason for rejection...')}
            rows={2}
            autoFocus
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="mod-panel-actions">
        {!showRejectForm ? (
          <>
            <button
              className="modifications-btn-reject"
              onClick={() => setShowRejectForm(true)}
              disabled={submitting}
            >
              {t('modifications.detail.rejectBtn', 'Reject')}
            </button>
            <button
              className="modifications-btn-confirm"
              onClick={handleApprove}
              disabled={submitting}
            >
              {submitting ? t('common.loading', 'Loading...') : t('modifications.quoteApproval.approveBtn', 'Approve')}
            </button>
          </>
        ) : (
          <>
            <button
              className="mod-panel-btn-cancel"
              onClick={() => setShowRejectForm(false)}
              disabled={submitting}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              className="modifications-btn-reject"
              onClick={handleReject}
              disabled={submitting || !rejectReason.trim()}
            >
              {submitting ? t('common.loading', 'Loading...') : t('modifications.detail.confirmReject', 'Confirm Rejection')}
            </button>
          </>
        )}
      </div>

      {/* Trade detail modal */}
      {showDetailModal && (
        <TradeDetailViewModal
          trade={trade}
          entityId={entityId}
          onApproveSuccess={() => { setShowDetailModal(false); onApproveSuccess(); }}
          onRejectSuccess={() => { setShowDetailModal(false); onRejectSuccess(); }}
          onError={onError}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

export default QuoteApprovalDetailPanel;
